import React, { useContext, useState, useEffect } from "react";
import {
	handleForm,
	useGoogleReCaptcha,
	recaptchaSubmit,
	GoogleReCaptchaProvider,
} from "gatsby-frontend";
import { SiteContext } from "gatsby-frontend/src/Site";
import {
	Paper,
	Button,
	Grid,
	Alert,
	CircularProgress,
	Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { Field } from "./../../";

import * as yup from "yup";

const Label = ({ label = null, variant }) => {
	return <Typography variant={variant}>{label}</Typography>;
};

const getValidationSchema = ({ form_fields }) => {
	const initialValues = {};
	const yupObject = {};

	for (const [
		field_key,
		{ input, type, label, required, multiple, options },
	] of Object.entries(form_fields)) {
		if (required) {
			if (multiple || input === "cart") {
				initialValues[field_key] = [];
			} else {
				initialValues[field_key] = "";
			}
			if (input === "select" && multiple) {
				yupObject[field_key] = yup
					.array()
					.of(yup.string())
					.min(1, "Please select at least one option");
			} else if (input === "select") {
				yupObject[field_key] = yup.string().required(`${label} is required`);
			} else if (input === "cart") {
			} else {
				if (type === "phone") {
					yupObject[field_key] = yup
						.string()
						.min(10, "Invalid phone number")
						.matches(
							/^(1\s?)?((\([0-9]{3}\))|[0-9]{3})[\s-]?[\0-9]{3}[\s-]?[0-9]{4}$/g,
							"Use only numbers"
						)
						.required(`${label} is required`);
				} else if (type === "email") {
					yupObject[field_key] = yup
						.string()
						.email()
						.required(`${label} is required`);
				} else {
					yupObject[field_key] = yup.string().required(`${label} is required`);
				}
			}
		}
	}

	const validationSchema = yup.object(yupObject);

	return { initialValues, validationSchema };
};

function ValidationHandler({ id, form_key, form_fields }) {
	const { cartItems } = useContext(SiteContext);

	const isCart =
		typeof Object.entries(form_fields).find(
			([, field]) => field.input === "cart"
		) !== "undefined";

	const isEmptyCart = isCart && cartItems.length === 0;

	const [formState, setFormState] = useState(null);

	useEffect(() => {
		if (formState === null) {
			setFormState((state) =>
				getValidationSchema({ id, form_key, form_fields })
			);
		}
	}, [formState]);

	if (formState === null) {
		return <CircularProgress />;
	}

	if (isEmptyCart) {
		return <Alert severity="info">Your cart is empty</Alert>;
	}

	if (!process.env.GATSBY_RECAPTCHA_SITE_KEY) {
		return <Alert severity="error">Missing reCAPTCHA site key</Alert>;
	}

	return (
		<GoogleReCaptchaProvider
			reCaptchaKey={process.env.GATSBY_RECAPTCHA_SITE_KEY}
		>
			<Form
				id={id}
				form_key={form_key}
				form_fields={form_fields}
				validationSchema={formState.validationSchema}
				initialValues={formState.initialValues}
				isCart={isCart}
			/>
		</GoogleReCaptchaProvider>
	);
}

function Form({
	id,
	form_key,
	form_fields,
	validationSchema,
	initialValues,
	isCart,
}) {
	const { cartItems } = useContext(SiteContext);
	const [status, setStatus] = useState({});
	const { executeRecaptcha } = useGoogleReCaptcha();

	const form = useFormik({
		enableReinitialize: true,
		validationSchema,
		initialValues,
		onSubmit: async (values, { setSubmitting }) => {
			setSubmitting(true);

			const isValidRecaptcha = await recaptchaSubmit(
				executeRecaptcha,
				process.env.GATSBY_BACKEND_URL
			);

			if (!isValidRecaptcha) {
				setStatus({
					type: "error",
					message: "Invalid reCAPTCHA. Please try again.",
				});
				setSubmitting(false);
				return;
			} else {
				let form_cart = isCart
					? cartItems.map((item) => JSON.stringify(item))
					: [];

				let body = {
					form_id: id,
					form_key,
					form_data: values,
					form_cart,
				};
				let response = await handleForm({ body });
				let response_data = await response.json();
				if (response_data.error) {
					setStatus({
						type: "error",
						message: response_data.error,
					});
				} else {
					setStatus({
						type: "success",
						message: response_data.message,
					});
				}
			}
		},
	});

	return (
		<Paper
			component="form"
			onSubmit={form.handleSubmit}
			sx={(theme) => ({ padding: theme.spacing(4) })}
			className="form"
		>
			{status.type && (
				<Alert
					sx={(theme) => ({ marginBottom: theme.spacing(theme.spacing(3)) })}
					severity={status.type}
				>
					{status.message}
				</Alert>
			)}
			{(!status.type || (status.type && status.type !== "success")) && (
				<Grid container spacing={2}>
					{Object.entries(form_fields).map(([field_key, field_props]) => {
						let grid_w = field_props.width ? field_props.width : 4;
						if (field_props.input === "cart") grid_w = 12;
						return (
							<Grid item xs={grid_w} key={field_key}>
								{field_props.type === "label" ? (
									<Label {...field_props} />
								) : (
									<Field form={form} field_key={field_key} {...field_props} />
								)}
							</Grid>
						);
					})}
					<Grid
						item
						xs={12}
						sx={(theme) => ({
							marginTop: theme.spacing(2),
						})}
					>
						<Button
							type="submit"
							color="primary"
							variant="contained"
							sx={{ display: "block", width: "100%" }}
							size="large"
							disabled={form.isSubmitting}
						>
							Submit
						</Button>
					</Grid>
				</Grid>
			)}
		</Paper>
	);
}

export default ValidationHandler;
